import React, { useState, useEffect, useCallback } from "react";
import {
  Mail,
  Send,
  AlertTriangle,
  CheckCircle2,
  FileText,
  X,
  Plus,
  Trash2,
  Eye,
  Edit3,
  Loader2,
  Lock,
} from "lucide-react";
import {
  previewCommunication,
  createCommunication,
  sendCommunication,
} from "@/lib/recruitmentApi";
import { getApiErrorMessage, logAppError } from "@/lib/api";

export default function EmailPreviewModal({
  isOpen,
  onClose,
  type = "refusal", // "refusal" | "client_presentation" | "generic"
  candidateId,
  applicationId,
  needId,
  defaultTo = [],
  defaultSubject = "",
  defaultBody = "",
  rejectionReason = "",
  attachments = [],
  onEmailSent,
}) {
  const [toList, setToList] = useState(defaultTo);
  const [newToInput, setNewToInput] = useState("");
  const [ccList, setCcList] = useState([]);
  const [newCcInput, setNewCcInput] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [reason, setReason] = useState(rejectionReason);

  const [previewData, setPreviewData] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("preview"); // "preview" | "edit"

  // 1. Fetch preview on open or reason change
  const fetchPreview = useCallback(async () => {
    if (!isOpen || !candidateId) return;

    setIsLoadingPreview(true);
    setError(null);
    try {
      const data = await previewCommunication({
        type,
        candidate_id: candidateId,
        application_id: applicationId || undefined,
        need_id: needId || undefined,
        to: defaultTo.length > 0 ? defaultTo : undefined,
        subject: defaultSubject || undefined,
        body: defaultBody || undefined,
        rejection_reason: reason.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      setPreviewData(data);
      setSubject((prev) => prev || data.subject);
      setBody((prev) => prev || data.body);
      setToList((prev) => (prev.length > 0 ? prev : data.to || []));
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de charger l'aperçu"));
      logAppError("EmailPreviewModal.fetchPreview", err);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [
    isOpen,
    candidateId,
    type,
    applicationId,
    needId,
    defaultTo,
    defaultSubject,
    defaultBody,
    reason,
    attachments,
  ]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  if (!isOpen) return null;

  // 2. Add recipient
  const handleAddTo = () => {
    const val = newToInput.trim().toLowerCase();
    if (val && !toList.includes(val)) {
      setToList([...toList, val]);
      setNewToInput("");
    }
  };

  const handleAddCc = () => {
    const val = newCcInput.trim().toLowerCase();
    if (val && !ccList.includes(val)) {
      setCcList([...ccList, val]);
      setNewCcInput("");
    }
  };

  // 3. Confirm and send
  const handleConfirmSend = async () => {
    if (isSending) return; // double-click protection
    setIsSending(true);
    setError(null);

    try {
      // Step A: Create communication record
      const createData = await createCommunication({
        type,
        candidate_id: candidateId,
        application_id: applicationId || undefined,
        need_id: needId || undefined,
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        subject: subject.trim(),
        body: body,
        rejection_reason: reason.trim() || undefined,
        attachments: attachments,
      });

      // Step B: Send communication
      const sendData = await sendCommunication(createData.communication_id);

      if (onEmailSent) onEmailSent(sendData);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "Erreur lors de l'envoi"));
      logAppError("EmailPreviewModal.handleConfirmSend", err);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div className="max-w-2xl w-full bg-[#14183a] border border-slate-700/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <span>
                  {type === "refusal" && "Email de refus de candidature"}
                  {type === "client_presentation" && "Email de présentation client"}
                  {type === "generic" && "Communication Recrutement"}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/30 text-violet-300">
                  Aperçu obligatoire
                </span>
              </h3>
              <p className="text-xs text-slate-400">
                Vérifiez et confirmez le message avant tout envoi définitif.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body content */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Recipients */}
          <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Destinataires (À)</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {toList.map((addr, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 rounded-lg bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs flex items-center gap-1.5"
                  >
                    <span>{addr}</span>
                    <button
                      onClick={() => setToList(toList.filter((_, i) => i !== idx))}
                      className="hover:text-red-400"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newToInput}
                  onChange={(e) => setNewToInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTo())}
                  placeholder="Ajouter une adresse email..."
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleAddTo}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Ajouter</span>
                </button>
              </div>
            </div>

            {/* Optional CC */}
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">Copie (CC)</label>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {ccList.map((addr, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-0.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 text-xs flex items-center gap-1"
                  >
                    <span>{addr}</span>
                    <button
                      onClick={() => setCcList(ccList.filter((_, i) => i !== idx))}
                      className="hover:text-red-400"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newCcInput}
                  onChange={(e) => setNewCcInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCc())}
                  placeholder="Ajouter une adresse en copie..."
                  className="flex-1 px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleAddCc}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-medium"
                >
                  Ajouter CC
                </button>
              </div>
            </div>
          </div>

          {/* Optional Rejection Reason field for Refusal emails */}
          {type === "refusal" && (
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1">
                Motif du refus (facultatif — modèle générique si vide)
              </label>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: Le client recherche un profil disposant d'au moins 5 ans d'expérience sur AWS..."
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                💡 Règle d'éthique : le score IA de matching n'est jamais utilisé pour motiver un refus.
              </p>
            </div>
          )}

          {/* Subject & View toggle */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex-1 mr-3">
              <label className="block text-xs font-semibold text-slate-300 mb-1">Objet de l'email</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-xs focus:border-violet-500 focus:outline-none"
              />
            </div>
            <div className="flex rounded-lg bg-slate-900 p-1 border border-slate-800 text-xs self-end mb-0.5">
              <button
                onClick={() => setViewMode("preview")}
                className={`px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${
                  viewMode === "preview" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Aperçu HTML</span>
              </button>
              <button
                onClick={() => setViewMode("edit")}
                className={`px-3 py-1 rounded-md transition-colors flex items-center gap-1 ${
                  viewMode === "edit" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Modifier le texte</span>
              </button>
            </div>
          </div>

          {/* Render Preview or Edit HTML */}
          {isLoadingPreview ? (
            <div className="p-8 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
            </div>
          ) : viewMode === "preview" ? (
            <div className="p-4 rounded-xl bg-white text-slate-900 text-xs shadow-inner min-h-[180px] max-h-[300px] overflow-y-auto">
              <div dangerouslySetInnerHTML={{ __html: body }} />
            </div>
          ) : (
            <textarea
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full p-3 rounded-xl bg-slate-950 font-mono text-xs text-slate-200 border border-slate-700 focus:border-violet-500 focus:outline-none leading-relaxed"
            />
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="p-3 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-slate-300">
                <FileText className="w-4 h-4 text-violet-400" />
                <span>Pièce jointe certifiée : {attachments[0].filename}</span>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                Immuable L4
              </span>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-5 border-t border-slate-800 bg-slate-900/60 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirmSend}
            disabled={isSending || toList.length === 0}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-medium text-xs flex items-center gap-2 shadow-lg shadow-violet-600/20 disabled:opacity-50 transition-all"
          >
            {isSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Confirmer l'envoi</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
